import { Controller } from "@hotwired/stimulus"

const PARAMETER_FIELD_PREFIX = "report_params["

// Backs a custom report's "Clear Filters" button. Clearing blanks every parameter field —
// including values prefilled as inferred defaults — while any other field, such as the
// enterprise companies checkboxes, returns to the state it was rendered in. The button is
// disabled while there is nothing to clear. After clearing, the report re-runs when
// `runnable` (no parameter is required); otherwise the stale result is emptied rather than
// replaced by a list of "required" errors. Either way a run still in flight is stopped
// first, so its response cannot land afterward and restore results for the old filters.
export default class extends Controller {
  static targets = ["button"]
  static values = { runnable: Boolean }

  clear() {
    this.submission?.stop()
    this.fields.forEach((field) => this.restore(field))
    this.refresh()
    this.dispatch("clear")
    if (this.runnableValue) {
      this.element.requestSubmit()
    } else {
      document.getElementById("report-result")?.replaceChildren()
    }
  }

  connect() {
    this.refresh()
  }

  // Disabled fields never post (an options_query select awaiting its scope), and hidden
  // ones carry scope rather than a filter choice.
  get fields() {
    return Array.from(this.element.elements).filter(
      (field) =>
        field.name &&
        !field.disabled &&
        !["button", "hidden", "reset", "submit"].includes(field.type)
    )
  }

  isParameter(field) {
    return field.name.startsWith(PARAMETER_FIELD_PREFIX)
  }

  isRestored(field) {
    if (field.type === "checkbox" || field.type === "radio") {
      return field.checked === this.restoredChecked(field)
    }
    if (field.tagName === "SELECT") {
      return Array.from(field.options).every(
        (option) => option.selected === this.restoredSelected(field, option)
      )
    }
    return field.value === this.restoredValue(field)
  }

  refresh() {
    this.buttonTarget.disabled = this.fields.every((field) => this.isRestored(field))
  }

  restore(field) {
    if (field.type === "checkbox" || field.type === "radio") {
      field.checked = this.restoredChecked(field)
    } else if (field.tagName === "SELECT") {
      Array.from(field.options).forEach(
        (option) => (option.selected = this.restoredSelected(field, option))
      )
    } else {
      field.value = this.restoredValue(field)
    }
  }

  restoredChecked(field) {
    return this.isParameter(field) ? false : field.defaultChecked
  }

  // A cleared single select lands on its blank option (include_blank, or tristate's "Leave
  // unchanged"); a cleared multi-select has nothing selected. A single select rendered with
  // no option marked selected shows its first.
  restoredSelected(field, option) {
    if (this.isParameter(field)) return !field.multiple && option.value === ""
    if (field.multiple || Array.from(field.options).some((o) => o.defaultSelected)) {
      return option.defaultSelected
    }
    return option === field.options[0]
  }

  restoredValue(field) {
    return this.isParameter(field) ? "" : field.defaultValue
  }

  submissionEnded({ detail: { formSubmission } }) {
    if (this.submission === formSubmission) this.submission = null
  }

  // Turbo's FormSubmission#stop aborts the fetch, and with it any Turbo Stream response not
  // yet rendered.
  submissionStarted({ detail: { formSubmission } }) {
    this.submission = formSubmission
  }
}
